import { Injectable, OnInit } from '@angular/core';
import { FormControlStatus } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { BaseComponent } from '@core/base.component';
import { SnackbarService } from '@services/ui/snackbar.service';
import { ICON } from '@core/util/icon';
import LogRocket from 'logrocket';
import { Observable } from 'rxjs';
import { CreateStatusType } from '@state/utils/models';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

interface VM {
	loading: boolean;
}

@UntilDestroy()
@Injectable()
export abstract class BaseRequisitionModal<T extends { id: string }>
	extends BaseComponent<VM>
	implements OnInit
{
	public formStatus: FormControlStatus;
	protected abstract statusState$:
		| Observable<CreateStatusType>
		| Observable<CreateStatusType>[];
	private updatedRequisition: T;

	constructor(
		protected dialogRef: MatDialogRef<unknown, string>,
		protected snackbarService: SnackbarService,
	) {
		super({ loading: false });
	}

	ngOnInit(): void {
		if (Array.isArray(this.statusState$)) {
			for (const statusState$ of this.statusState$) {
				this.subscribeToStatusChanges(statusState$);
			}
		} else {
			this.subscribeToStatusChanges(this.statusState$);
		}
	}

	get isDisabled() {
		return this.formStatus !== 'VALID' || this.VM.loading;
	}

	abstract onSubmitAction(requisition: T): T;
	abstract successMessage: string;
	abstract errorMessage: string;

	async onSubmit(requisition: T) {
		this.VM = {
			loading: true,
		};
		this.updatedRequisition = this.onSubmitAction(requisition);
	}

	protected afterSuccessfulSubmit(requisition: T) {
		this.dialogRef.close(requisition.id);
	}

	closeModal() {
		this.dialogRef.close();
	}

	onFormStatusChange(status: FormControlStatus) {
		this.formStatus = status;
	}

	private subscribeToStatusChanges(
		statusObservable: Observable<CreateStatusType>,
	) {
		statusObservable.pipe(untilDestroyed(this)).subscribe((status) => {
			switch (status) {
				case CreateStatusType.success: {
					this.VM = {
						loading: false,
					};
					this.snackbarService.openSnackBar({
						text: this.successMessage,
						type: 'success',
						icon: ICON.CIRCLE_CHECK,
					});
					this.afterSuccessfulSubmit(this.updatedRequisition);
					break;
				}
				case CreateStatusType.failed: {
					this.VM = {
						loading: false,
					};
					LogRocket.error(this.errorMessage);
					this.snackbarService.openSnackBar({
						text: `${this.errorMessage}. If the error persists, please contact customer support`,
						type: 'error',
						icon: ICON.CIRCLE_X,
					});
					break;
				}
				case CreateStatusType.creating: {
					this.VM = {
						loading: true,
					};
					break;
				}
			}
		});
	}
}
